import _ from 'lodash';
import config from 'client/shared/config';
import { observable, action, computed } from 'mobx';

import { TIERS } from './artist-ratings-constants';

import UserStore from './user-store';

const removeBadChars = (value) => {
	return value
		.replace(/['|"|,|`]/g, '')
		.trim()
		.replace(/\s/g, '_');
};

export default class Artist {
	store = null;
	user = null;

	constructor (store, data) {
		this.store = store;

		if (_.isEmpty(data)) {

			return;
		}

		this.update(data);
	}

	_id = null;

	get id () {
		return this._id;
	}

	set id (id) {
		this._id = id;
	}

	/**
	 * Observables & Computeds
	 */

	@observable
	_name = '';

	@computed
	get name () {
		return this._name;
	}

	set name (name) {
		this._name = name;
	}

	@observable
	_userID = '';

	@computed
	get userID () {
		return this._userID;
	}

	set userID (userID) {
		this._userID = userID;
	}

	@observable
	_hfmPicksOptIn = true;

	@computed
	get hfmPicksOptIn () {
		return this._hfmPicksOptIn;
	}

	set hfmPicksOptIn (hfmPicksOptIn) {
		this._hfmPicksOptIn = hfmPicksOptIn;
	}

	/**
	 * This field is used for display and for select menus.
	 * The value is an object, except when used in a genre select menu,
	 * then it is a string.
	 * @type Object
	 */
	@observable
	_genre = null;

	@computed
	get genre () {
		return this._genre;
	}

	set genre (genre) {
		this._genre = genre;
	}

	/**
	 * This is the field that saves  and is derived from genre
	 * @type String
	 */
	@computed
	get genreID () {
		const { genre } = this;

		if (_.isString(genre)) {

			return genre;
		} else if(_.isObject(genre)) {

			return genre._id;
		}

		return null;
	}

	@observable
	_paymentServiceID = '';

	@computed
	get paymentServiceID () {
		return this._paymentServiceID;
	}

	set paymentServiceID (paymentServiceID) {
		this._paymentServiceID = paymentServiceID;
	}

	@observable
	_isExclusive = false;

	@computed
	get isExclusive () {
		return this._isExclusive;
	}

	set isExclusive (isExclusive) {
		this._isExclusive = isExclusive;
	}

	@observable
	_isFeatured = false;

	@computed
	get isFeatured () {
		return this._isFeatured;
	}

	set isFeatured (isFeatured) {
		this._isFeatured = isFeatured;
	}

	@observable
	_featuredBio = '';

	@computed
	get featuredBio () {
		return this._featuredBio;
	}

	set featuredBio (featuredBio) {
		this._featuredBio = featuredBio;
	}
	
	@observable
	_tier = 'In Review';

	@computed
	get tier () {
		return this._tier;
	}

	set tier (tier) {
		this._tier = tier;
	}

	@observable
	_bio = '';

	@computed
	get bio () {
		return this._bio;
	}

	set bio (bio) {
		this._bio = bio;
	}

	@observable
	_photos = [];

	@computed
	get photos () {
		return this._photos;
	}

	set photos (photos) {
		this._photos = photos;
	}

	@observable
	_hifives = 0;

	@computed
	get hifives () {
		return this._hifives;
	}

	set hifives (hifives) {
		this._hifives = hifives;
	}

	@observable
	_status = 'PENDING';

	@computed
	get status () {
		return this._status;
	}

	set status (status) {
		this._status = status;
	}

	@observable
	_user = '';

	@computed
	get user () {
		return this._user;
	}

	set user (user) {
		this._user = user;
	}

	@observable
	_scoreMatrix = [];

	@computed
	get scoreMatrix () {
		return this._scoreMatrix;
	}

	set scoreMatrix (scoreMatrix) {
		this._scoreMatrix = scoreMatrix;
	}

	@observable
	_photoData = '';

	@computed
	get photoData () {
		return this._photoData;
	}

	set photoData (photoData) {
		this._photoData = photoData;

		if (_.isEmpty(this.id)) {

			return;
		}

		this.store.saveArtistPhoto(this);
	}

	@observable
	_uploadSongs = '';

	@computed
	get uploadSongs () {
		return this._uploadSongs;
	}

	set uploadSongs (uploadSongs) {
		this._uploadSongs = uploadSongs;
	}

	@observable
	_albums = '';

	@computed
	get albums () {
		return this._albums;
	}

	set albums (albums) {
		this._albums = albums;
	}


	@observable
	_songs = '';

	@computed
	get songs () {
		return this._songs;
	}

	set songs (songs) {
		this._songs = songs;
	}


	@observable
	_searchStatus = {
		isSearchable: false
	};

	@computed
	get searchStatus () {
		return this._searchStatus;
	}

	@computed
	get isSearchable () {
		this.searchStatus.isSearchable;
	}

	set searchStatus (searchStatus) {
		this._searchStatus = searchStatus;
	}



	@observable
	_bannerImage = '';

	@observable
	_bannerImageCacheBust = 0;

	@computed
	get bannerImage () {
		return this._bannerImage;
	}

	set bannerImage (bannerImage) {
		this._bannerImage = bannerImage;
	}

	@observable
	_bannerImageData = '';

	@computed
	get bannerImageData () {
		return this._bannerImageData;
	}

	set bannerImageData (bannerImageData) {
		this._bannerImageData = bannerImageData;

		this._bannerImageData = bannerImageData;

		if (_.isEmpty(this.id)) {

			return;
		}

		this.store.saveBannerImage(this);
	}

	@computed
	get bannerImagePath () {
		if (this.isBannerImageEmpty) {

			return ''
		}

		const { paths: { bannerImgBasePath } } = config;
		const raw = String(this._bannerImage).trim();
		const q = raw.indexOf('?');
		const pathOnly = q >= 0 ? raw.slice(0, q) : raw;
		const serverQs = q >= 0 ? raw.slice(q + 1) : '';
		const bust = this._bannerImageCacheBust;
		const qsParts = [serverQs, bust ? `cb=${bust}` : ''].filter(Boolean);
		const qs = qsParts.length ? qsParts.join('&') : '';
		return qs ? `${bannerImgBasePath}/${pathOnly}?${qs}` : `${bannerImgBasePath}/${pathOnly}`;
	}

	@computed
	get isBannerImageEmpty() {
		return _.isEmpty(this.bannerImage);
	}

	// Mobile-specific banner (~9:16 portrait). Mirrors the web banner pattern:
	// setting *Data triggers an upload via the store; *Path is the public URL
	// with cache-busting. Optional — when empty, mobile UI falls back to the
	// regular bannerImagePath.
	@observable
	_mobileBannerImage = '';

	@observable
	_mobileBannerImageCacheBust = 0;

	@computed
	get mobileBannerImage () {
		return this._mobileBannerImage;
	}

	set mobileBannerImage (mobileBannerImage) {
		this._mobileBannerImage = mobileBannerImage;
	}

	@observable
	_mobileBannerImageData = '';

	@computed
	get mobileBannerImageData () {
		return this._mobileBannerImageData;
	}

	set mobileBannerImageData (mobileBannerImageData) {
		this._mobileBannerImageData = mobileBannerImageData;

		if (_.isEmpty(this.id)) {
			return;
		}

		this.store.saveMobileBannerImage(this);
	}

	@computed
	get mobileBannerImagePath () {
		if (_.isEmpty(this._mobileBannerImage)) {
			return '';
		}

		const { paths: { bannerImgBasePath } } = config;
		const raw = String(this._mobileBannerImage).trim();
		const q = raw.indexOf('?');
		const pathOnly = q >= 0 ? raw.slice(0, q) : raw;
		const serverQs = q >= 0 ? raw.slice(q + 1) : '';
		const bust = this._mobileBannerImageCacheBust;
		const qsParts = [serverQs, bust ? `cb=${bust}` : ''].filter(Boolean);
		const qs = qsParts.length ? qsParts.join('&') : '';
		return qs ? `${bannerImgBasePath}/${pathOnly}?${qs}` : `${bannerImgBasePath}/${pathOnly}`;
	}

	@computed
	get songDirectory () {
		return removeBadChars(`${this._id}/${this.name}`);
	}

	@computed
	get imagePath () {
		const { paths: { profileImgBasePath } } = config;
		return `${profileImgBasePath}/${this.user.profileImage}`;
	}

	// @deprecated - use artist.bannerImagePath
	@computed
	get bannerPath () {
		return this.bannerImagePath;
	}

	@computed
	get totalScore () {
		return _(this.scoreMatrix)
			.map(({ score }) => score)
			.reduce((sum, val) => sum + val, 0);
	}

	// @computed
	// get tier () {
	// 	const totalScore  = this.totalScore;
	//
	// 	if (totalScore <= 39) {
	//
	// 		return TIERS.NOT_APPROVED;
	// 	}
	//
	// 	if (totalScore === 40 || totalScore === 41) {
	//
	// 		return TIERS.IN_REVIEW;
	// 	}
	//
	// 	if (totalScore >= 42 && totalScore <= 58) {
	//
	// 		return TIERS.TIER_2;
	// 	}
	//
	// 	if (totalScore >= 59) {
	//
	// 		return TIERS.TIER_1;
	// 	}
	// }

	@computed
	get serialized () {
		return {
			_id: this.id,
			name: this.name,
			isExclusive: this.isExclusive,
			isFeatured: this.isFeatured,
			featuredBio: this.featuredBio,
			bannerImage: this.bannerImage,
			userID: this.userID,
			hfmPicksOptIn: this.hfmPicksOptIn,
			genre: this.genre,
			genreID: this.genreID,
			paymentServiceID: this.paymentServiceID,
			tier: this.tier,
			bio: this.bio,
			photos: this.photos,
			hifives: this.hifives,
			status: this.status,
			user: this.user.serialized,
			scoreMatrix: this.scoreMatrix,
			albums: this.albums,
			songs: this.songs
		};
	}

	@computed
	get jsonString () {
		return JSON.stringify(_.omit(this.serialized, ['_id']));
	}

	/**
	 * Actions
	 */

 	@action.bound
 	async save (opts = {}) {
 		await this.store.saveArtist(this, opts);
 	}

 	@action.bound
 	async delete () {
 		await this.store.delete(this);
 	}

 	@action.bound
	async removePhoto (photo) {
 		artist.photos.remove(photo);
 		await this.save();
 	}

	@action.bound
	async approve () {
		await this.store.approveArtist(this);
	}

	@action.bound
	setCategoryRating (label, score) {
		const itemIndex = _.findIndex(artist.scoreMatrix, (item) =>  item.label === label);
		artist.scoreMatrix[itemIndex].score = score;
	}

	@action.bound
	update ({
		_id,
		name,
		userID,
		isExclusive,
		isFeatured,
		featuredBio,
		bannerImage,
		bannerImageData,
		mobileBannerImage,
		mobileBannerImageData,
		hfmPicksOptIn,
		genre,
		genreID,
		paymentServiceID,
		tier,
		bio,
		photos,
		hifives,
		status,
		user,
		scoreMatrix,
		albums,
		songs,
		searchStatus
	}) {

		if (_.isEmpty(user)) {

			user = UserStore.getItemByID(userID);
		}

		this._id = _.defaultTo(_id, this.id);
		this._name = _.defaultTo(name, this.name);
		this._userID = _.defaultTo(userID, this.userID);
		this._isExclusive = _.defaultTo(isExclusive, this.isExclusive);
		this._isFeatured= _.defaultTo(isFeatured, this.isFeatured);
		this._featuredBio= _.defaultTo(featuredBio, this.featuredBio);
		if (bannerImage !== undefined) {

			this._bannerImageCacheBust = 0;
		}
		this._bannerImage = _.defaultTo(bannerImage, this.bannerImage);
		this._bannerImageData = _.defaultTo(bannerImageData, this.bannerImageData);
		if (mobileBannerImage !== undefined) {
			this._mobileBannerImageCacheBust = 0;
		}
		this._mobileBannerImage = _.defaultTo(mobileBannerImage, this.mobileBannerImage);
		this._mobileBannerImageData = _.defaultTo(mobileBannerImageData, this.mobileBannerImageData);
		this._hfmPicksOptIn = _.defaultTo(hfmPicksOptIn, this.hfmPicksOptIn);
		this._genre = _.defaultTo(genre, this.genre);
		this._genreID = _.defaultTo(genreID, this.genreID);
		this._paymentServiceID = _.defaultTo(paymentServiceID, this.paymentServiceID);
		this._tier = _.defaultTo(tier, this.tier);
		this._bio = _.defaultTo(bio, this.bio);
		this._photos = _.defaultTo(photos, this.photos);
		this._hifives = _.defaultTo(hifives, this.hifives);
		this._status = _.defaultTo(status, this.status);
		this._user = _.defaultTo(user, this.user);
		this._scoreMatrix = _.defaultTo(scoreMatrix, this.scoreMatrix);
		this._albums = _.defaultTo(albums, this.albums);
		this._songs = _.defaultTo(songs, this.songs);
		this._searchStatus = _.defaultTo(searchStatus, this.searchStatus);
	}
}
